/**
 * The borrower's loan screen's controller: `borrowedLoans.model.ts` wired to
 * `fetchBorrowedLoans` for the read and the four acts for the writes.
 *
 * It owns what the model cannot: which row is mid-flight and which way, and the
 * round counter that re-reads afterwards.
 *
 * NO THUMBNAILS, and the absence is deliberate rather than pending.
 * `ondo.borrower_loans()` is the loan ledger and returns no image path, so
 * signing one would need a second read of the works — and on a declined or
 * returned loan that read is refused, because the grant ended with the loan. A
 * list whose pictures appeared and then vanished as rows finished would read as
 * breakage. The estate's own loan rail shows none either.
 *
 * ONE WRITE PATH FOR ALL FOUR ACTS, which is not a shortcut. The four differ
 * only in which function they call: each is a single transition on a row named
 * in the path, with no body, no reason and no date — see the controller in
 * `apps/api/src/loans` for why none of them may carry one. Four near-identical
 * handlers would have been four places for the re-read to drift.
 *
 * AFTER EVERY ACT IT RE-READS, and here a row does not always leave. Accepting
 * moves 'proposed' to 'agreed' and the row STAYS, with different controls;
 * declining and returning end it and the row stays too, finished. That is the
 * opposite of `useTransferOffersController`, where "a row does not change when
 * answered, it leaves" — a loan's ledger is a ledger rather than an inbox, and
 * holds its history on purpose.
 */

import { useCallback, useEffect, useState } from "react";
import type { BorrowedLoan } from "@ondo/shared";

import {
  acceptLoan,
  declineLoan,
  fetchBorrowedLoans,
  markLoanReceived,
  markLoanReturned,
} from "../lib/api.ts";
import { describeBorrowedError, toBorrowedLoansView } from "./borrowedLoans.model.ts";
import type { BorrowedLoanAct, BorrowedLoansView } from "./borrowedLoans.model.ts";

export interface BorrowedLoansController {
  view: BorrowedLoansView;
  /** Record one of the four acts against one loan. */
  act: (loanId: string, act: BorrowedLoanAct) => void;
  /** Which row is mid-flight, and which way, so its controls can be disabled. */
  busy: { loanId: string; act: BorrowedLoanAct } | null;
}

/**
 * The call each act makes. One place, so a fifth act is one line.
 *
 * A FUNCTION AND NOT A MODULE-LEVEL MAP, which it was first and which broke
 * `WorkspaceRoute.test.tsx` the moment this screen was mounted on a portal. A
 * `Record<Act, typeof acceptLoan>` reads these four bindings when the module is
 * IMPORTED, so every test that partially mocks `../lib/api.ts` had to list all
 * four whether it pressed anything or not — and the failure arrives as a mock
 * error in an unrelated file. Reading them at call time costs nothing and keeps
 * the coupling where the call is.
 *
 * Exhaustive over the union rather than defaulted, so a fifth act is a type
 * error here before it is a silently dead button.
 */
function callFor(act: BorrowedLoanAct, loanId: string): Promise<unknown> {
  switch (act) {
    case "accept":
      return acceptLoan(loanId);
    case "decline":
      return declineLoan(loanId);
    case "received":
      return markLoanReceived(loanId);
    case "returned":
      return markLoanReturned(loanId);
  }
}

/**
 * Loans offered to this account, and the four answers to them.
 *
 * ONE READ AND NOT TWO, unlike the title-offers screen next door: accepting a
 * loan needs nothing chosen. A loan moves possession and not title, so there is
 * no collection to land it in and nothing to populate before the controls are
 * honest — which is the distinction `ondo.loans` was built on.
 *
 * @param workBase '/gallery/ONDO-1234/work' — where a row links to.
 */
export function useBorrowedLoansController(workBase: string): BorrowedLoansController {
  const [loans, setLoans] = useState<BorrowedLoan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<{ loanId: string; act: BorrowedLoanAct } | null>(
    null,
  );
  /** Bumped to re-read after an act, so the list reflects Postgres. */
  const [round, setRound] = useState(0);

  useEffect(() => {
    let active = true;

    fetchBorrowedLoans()
      .then((found) => {
        if (active) setLoans(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeBorrowedError(cause));
      });

    return () => {
      active = false;
    };
  }, [round]);

  const act = useCallback((loanId: string, which: BorrowedLoanAct) => {
    setBusy({ loanId, act: which });
    setError(null);

    callFor(which, loanId)
      .then(() => {
        // The RE-READ and not the returned row, even though three of the four
        // routes hand one back. Accepting writes the borrower's work grant in
        // the same transaction, so what the caller may see changes with the act
        // — and the row that renders should be the row the ledger returns now,
        // not the one the write echoed.
        setLoans(null);
        setRound((n) => n + 1);
      })
      .catch((cause: unknown) => {
        setError(describeBorrowedError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  }, []);

  return {
    view: toBorrowedLoansView({ loans, error, workBase }),
    act,
    busy,
  };
}
